import {clamp, json, texture} from "p3";
import { Emitter } from "pixi-particles";
import common from "../common";
import definitions from "../definitions";
import settings from "../settings";
import LocationButton from "../ui/map/LocationButton";
import TutorialMapControls from "../ui/tutorial/TutorialMapControls";
import {iswebfont, locale, resizeLabelToFitText} from "../utils";
import GameScene from "./GameScene";
import MenuScene from "./MenuScene";

export default class MapScene extends MenuScene {
    constructor() {
        super("map");

        this._locationButtons = [];

        this._camera = null;
        this._mapLayer = null;
        this._buttonLayer = null;
        
        this._mapWidth = 0;
        this._mapHeight = 0;
        
        this._cameraTarget = new PIXI.Point();
        this._cameraTargetEase = 12;
        
        this._mapAttractFxInit = false;
        this._mapDragEnabled = false;
        this._mapDragOffset = new PIXI.Point();

        this._mapDragDistanceCovered = 0;

        this._pin = null;

        this._defaultTween = null;

        this._tutorial = null;

        this._zoomToLocationTween = null;

        this._areaLabel1 = null;
        this._areaLabel2 = null;
        this._areaLabel3 = null;

        this._tempButton1 = null;
        this._tempButton2 = null;

        this._zoomInButton = null;
        this._zoomOutButton = null;

        this._closeButton = null;
    }

    init() {
        super.init();
    }

    onSceneLoaded() {
        super.onSceneLoaded();

        if (!common.saveData.settings.musicMute) {
            P3.audio.playMusic("music_map_loop");
        }
        P3.audio.playSound(['vo_iv_hmm_00', 'vo_iv_hmm_01', 'vo_iv_hmm_02']);

        this.interactive = true;

        this.createFill();

        this._camera = new P3.Camera(new PIXI.Point(common.STAGE_WIDTH * 0.5, P3.View.height * 0.5), new PIXI.Point(0, 0));
        this._camera.zoom = 1.0;
        
        this._defaultTween = gsap.timeline();
        this.addTween(this._defaultTween);

        this._zoomToLocationTween = gsap.to(this._camera, 0.5, {
            zoom: 1.3,
            delay: 0.8,
            ease: Power0.easeInOut,
            onComplete: () => this._enableZoomButtons()
        });

        this._defaultTween.add(this._zoomToLocationTween, 0);

        this._mapLayer = this._addEmptyLayer(new PIXI.Point(1, 1));
        this._buttonLayer = this._addEmptyLayer(new PIXI.Point(1, 1));

        const map = new PIXI.Sprite(texture("map"));
        this._mapLayer.view.addChild(map);

        this._mapWidth = map.texture.width;
        this._mapHeight = map.texture.height;

        if (!iswebfont()) {
            this._areaLabel1 = new PIXI.BitmapText(locale('level_svan_land_area_label').toUpperCase(), {
                fontName: "Alghera Pro",
                fontSize: 54,
                align: "center",
                tint: 0x0
            });
        } else {
            this._areaLabel1 = new PIXI.Text(locale('level_svan_land_area_label').toUpperCase(), {
                fontFamily: "FredFredBurger",
                fontSize: 54,
                align: "center",
                fill: 0x0
            });
        }
        this._areaLabel1.position.set(875, !iswebfont() ? 848 : 842);
        this._areaLabel1.anchor.set(0.5);
        map.addChild(this._areaLabel1);

        if (!iswebfont()) {
            this._areaLabel2 = new PIXI.BitmapText(locale('level_kingdom_stag_area_label'), {
                fontName: "Alghera Pro",
                fontSize: 38,
                align: "center",
                tint: 0x0,
                maxWidth: 300
            });
        } else {
            this._areaLabel2 = new PIXI.Text(locale('level_kingdom_stag_area_label').toUpperCase(), {
                fontFamily: "FredFredBurger",
                fontSize: 38,
                align: "center",
                fill: 0x0,
                wordWrap: true,
                wordWrapWidth: 300
            });
        }
        this._areaLabel2.position.set(270, !iswebfont() ? 970 : 964);
        this._areaLabel2.anchor.set(0.5);
        map.addChild(this._areaLabel2);

        if (!iswebfont()) {
            this._areaLabel3 = new PIXI.BitmapText(locale('level_forest_outlaws_area_label'), {
                fontName: "Alghera Pro",
                fontSize: 40,
                align: "center",
                tint: 0x0,
                maxWidth: 200
            });
        } else {
            this._areaLabel3 = new PIXI.Text(locale('level_forest_outlaws_area_label'), {
                fontFamily: "FredFredBurger",
                fontSize: 40,
                align: "center",
                fill: 0x0,
                wordWrap: true,
                wordWrapWidth: 200
            });
        }
        this._areaLabel3.position.set(1490, !iswebfont() ? 608 : 602);
        this._areaLabel3.anchor.set(0.5);
        //map.addChild(this._areaLabel3);

        this._camera.target.x = this._cameraTarget.x = map.width * 0.5;
        this._camera.target.y = this._cameraTarget.y = map.height * 0.5;

        const locations = [
            definitions.LEVEL_ID.CASTLE_DUNGEON,
            definitions.LEVEL_ID.CASTLE_EXTERIOR,
            definitions.LEVEL_ID.CAVE_TUNNELS,
            definitions.LEVEL_ID.CAVE_HIDDEN_1,
            definitions.LEVEL_ID.CAVE_HIDDEN_2,
            definitions.LEVEL_ID.CAVE_HIDDEN_3,
            definitions.LEVEL_ID.CAVE_HIDDEN_4,
            definitions.LEVEL_ID.CAVE_HIDDEN_5,
            definitions.LEVEL_ID.CAVE_HIDDEN_6,
            definitions.LEVEL_ID.CAVE_MOLES,
            definitions.LEVEL_ID.FOREST_BOARIS_CAMP,
            definitions.LEVEL_ID.FOREST_CLEARING,
            definitions.LEVEL_ID.FOREST_DARK,
            definitions.LEVEL_ID.FOREST_LAKESIDE_EAST,
            definitions.LEVEL_ID.FOREST_LAKESIDE_WEST,
            definitions.LEVEL_ID.FOREST_MOLECAVE,
            definitions.LEVEL_ID.FOREST_OUTLAWS,
            definitions.LEVEL_ID.FOREST_SWAMP,
            definitions.LEVEL_ID.GNOME_CLEARING,
            definitions.LEVEL_ID.KINGDOM_STAG,
            definitions.LEVEL_ID.ROCKY_MOUNTAIN,
            definitions.LEVEL_ID.SVAN_LAND,
            definitions.LEVEL_ID.UNDERWATER_KINGDOM,
            definitions.LEVEL_ID.VILLAGE_FARM,
            definitions.LEVEL_ID.TUTORIAL
        ];

        const activeLocation = common.saveData.activeSlot.player.lastLocation;

        for (let i = locations.length-1; i >= 0; i--) {
            if (json(locations[i]).metadata.phase > settings.PHASE) {
                locations.splice(i, 1);
            }
        }

        if(locations.indexOf(definitions.LEVEL_ID.CAVE_HIDDEN_1) > -1 && !GameScene.active.questManager.isQuestComplete('quest_1', 'suzie')) {
            locations.splice(locations.indexOf(definitions.LEVEL_ID.CAVE_HIDDEN_1), 1);
        }
        if(locations.indexOf(definitions.LEVEL_ID.CAVE_HIDDEN_2) > -1 && !GameScene.active.questManager.isQuestComplete('quest_1', 'chick_of_doom')) {
            locations.splice(locations.indexOf(definitions.LEVEL_ID.CAVE_HIDDEN_2), 1);
        }
        if(locations.indexOf(definitions.LEVEL_ID.CAVE_HIDDEN_3) > -1 && !GameScene.active.questManager.isQuestComplete('quest_1', 'buff_sow')) {
            locations.splice(locations.indexOf(definitions.LEVEL_ID.CAVE_HIDDEN_3), 1);
        }
        if(locations.indexOf(definitions.LEVEL_ID.CAVE_HIDDEN_6) > -1 && !GameScene.active.questManager.isQuestComplete('quest_1', 'fish_monster')) {
            locations.splice(locations.indexOf(definitions.LEVEL_ID.CAVE_HIDDEN_6), 1);
        }

        if (common.saveData.activeSlot.unlocks == null) {
            common.saveData.activeSlot.unlocks = [];
        }
        for (let i in definitions.LEVEL_UNLOCKABLES) {
            if (locations.indexOf(definitions.LEVEL_UNLOCKABLES[i]) > -1 && 
                common.saveData.activeSlot.unlocks.indexOf(definitions.LEVEL_UNLOCKABLES[i]) < 0) {
                locations.splice(locations.indexOf(definitions.LEVEL_UNLOCKABLES[i]), 1);
            }
        }

        this._parseLocations(locations);

        this._closeButton = P3.Button.create(
            texture("btn_small_out"),
            null,
            null,
            texture("btn_icon_cross")
        );
        this._closeButton.overSoundName = "sfx_btn_generic_rollover";
        this._closeButton.clickSoundName = "sfx_btn_map_close";
        this._closeButton.signals.click.add(this.onCloseButtonClick, this);
        this.addChild(this._closeButton);

        this._zoomInButton = P3.Button.create(
            texture("btn_action_medium_out"),
            null,
            null,
            texture("btn_icon_zoom_in_medium")
        );
        this._zoomInButton.overSoundName = "sfx_btn_generic_rollover";
        this._zoomInButton.clickSoundName = "sfx_btn_generic_press_00";
        this._zoomInButton.signals.click.add(this.onZoomInButtonClick, this);
        this._zoomInButton.enable = false;
        this._zoomInButton.scale.set(0.8);
        if (settings.PHASE > 1) {
            this.addChild(this._zoomInButton);
        }

        this._zoomOutButton = P3.Button.create(
            texture("btn_action_medium_out"),
            null,
            null,
            texture("btn_icon_zoom_out_medium")
            );
        this._zoomOutButton.overSoundName = "sfx_btn_generic_rollover";
        this._zoomOutButton.clickSoundName = "sfx_btn_generic_press_00";
        this._zoomOutButton.signals.click.add(this.onZoomOutButtonClick, this);
        this._zoomOutButton.enable = false;
        this._zoomOutButton.scale.set(0.8);
        if (settings.PHASE > 1) {
            this.addChild(this._zoomOutButton);
        }

        this.createTitleBanner(locale('ui_map_title'));

        if(common.saveData.activeSlot.player.mapTutorial) {
            this._tutorial = new TutorialMapControls();
            this._tutorial.x = (common.STAGE_WIDTH / 2) + (P3.View.width / 2) - 200;
            this._tutorial.y = (common.STAGE_HEIGHT / 2) + (P3.View.height / 2) - 200;
            this.addChild(this._tutorial);
        }

        this.resize();
    }

    destroy() {
        this.removeTween(this._defaultTween);

        this._closeButton.destroy();

        P3.audio.stopMusic();

        if (this._tutorial) {
            this._tutorial.destroy();
        }

        super.destroy();
    }

    resize() {
        super.resize();

        if (this.isSceneLoaded) {
            this._closeButton.position.set(
                (common.STAGE_WIDTH + P3.View.width - this._closeButton.width) * 0.5 - this.buttonMargin.width,
                this._closeButton.height * 0.5 + this.buttonMargin.y
            );
            this._zoomInButton.position.set(
                (common.STAGE_WIDTH + P3.View.width - this._zoomInButton.width) * 0.5 - this.buttonMargin.width,
                (common.STAGE_HEIGHT + P3.View.height - this._zoomInButton.height) * 0.515 - this.buttonMargin.height,
            );
            this._zoomOutButton.position.set(
                (common.STAGE_WIDTH + P3.View.width - this._zoomOutButton.width - this._zoomInButton.width) * 0.48 - this.buttonMargin.width,
                (common.STAGE_HEIGHT + P3.View.height - this._zoomOutButton.height) * 0.515 - this.buttonMargin.height,
            );
        }
    }

    update() {
        super.update();

        if (this.isSceneLoaded) {
            this.updateCamera();

            for (let i = 0; i < this._locationButtons.length; ++i) {
                const locationButton = this._locationButtons[i];
                locationButton.attractFx && locationButton.attractFx.update(window.dt);
            }

            if(this._tutorial && this._mapDragDistanceCovered > 1000) {
                this._tutorial.destroy();
                this._tutorial = null;
                common.saveData.activeSlot.player.mapTutorial = false;
                common.saveData.saveGame();
            }
        }
    }

    updateCamera() {
        const touch = P3.input.touch;
        if (touch && this._zoomToLocationTween.progress() === 1) {
            if (!this._mapDragEnabled) {
                this._mapDragOffset = new PIXI.Point(touch.x + this._camera.target.x, touch.y + this._camera.target.y);
            }

            this._cameraTarget.x = this._mapDragOffset.x - touch.x;
            this._cameraTarget.y = this._mapDragOffset.y - touch.y;
        }
        this._mapDragEnabled = this._zoomToLocationTween.progress() === 1 ? touch : null;

        const zoom      = 1 / this._camera.zoom;
        const left      = P3.View.center.x * zoom;
        const right     = this._mapWidth - left;
        const top       = P3.View.center.y * zoom;
        const bottom    = this._mapHeight - top;
        const x         = clamp(this._cameraTarget.x, left, right);
        const y         = clamp(this._cameraTarget.y, top, bottom);
        const xDiff     = (x - this._camera.target.x) * this._cameraTargetEase * window.dt;
        const yDiff     = (y - this._camera.target.y) * this._cameraTargetEase * window.dt;

        this._camera.target.x += xDiff;
        this._camera.target.y += yDiff;

        this._camera.update();

        this._mapDragDistanceCovered += new P3.Vector2(xDiff, yDiff).length;
    }

    /**
     * @param {PIXI.Container} parentContainer 
     * @param {string} jsonFile 
     * @param {PIXI.Texture[]} textures 
     * @returns 
     */
    _createFx(parentContainer, jsonFile, textures=[]) {
        const emitter = new Emitter(parentContainer, textures, json(jsonFile));
        emitter.autoUpdate = false;
        return emitter;
    }

    /**
     * @param {string[]} ids
     * @private
     */
    _parseLocations(ids) {
        const activeLocation = common.saveData.activeSlot.player.lastLocation;

        for (let i = 0, count = ids.length; i < count; ++i) {
            const location = ids[i];
            let data;
            try {
                data = json(ids[i]);
            } catch {
                //console.log("[" + this.constructor.name + "]", "Location '" + location + "' not found");
            }

            if (data) {
                const position = new PIXI.Point(data.metadata.mapPosition.x, data.metadata.mapPosition.y);
                
                const button = this.createLocationButton(data.metadata.phase, location, locale(data.metadata.name).toUpperCase(), position);
                this._locationButtons.push(button);

                if (!settings.APP && data.metadata.phase > 1 && !common.saveData.activeSlot.unlocks.includes(location) && location !== activeLocation) {
                    button.attractFx = this._createFx(button, "fx_location_button_attract_00", [
                        texture("button_sparkle_attract_fx_00"), 
                        texture("button_sparkle_attract_fx_01")
                    ]);
                    button.attractFx.updateSpawnPos(-35, 15);
                }

                if (location === activeLocation) {
                    const pin = new PIXI.Sprite(texture("icon_map_current_location"));
                    pin.x = position.x;
                    pin.y = position.y - button.height + 50;
                    pin.anchor.set(0.5, 1);
                    pin.scale.set(0.75);
                    this._buttonLayer.view.addChild(pin);
                    this._cameraTarget.x = pin.x;
                    this._cameraTarget.y = pin.y;
                    this._buttonLayer.view.addChild(button);

                    this._defaultTween = gsap.timeline();
                    this.addTween(this._defaultTween);
    
                    this._defaultTween.add(gsap.to(pin.position, 0.5, {
                        x: pin.x,
                        y: pin.y - 25,
                        ease: Circ.easeInOut,
                    }), 0);
                }
            }
        }
    }

    /**
     * @param {string} id
     * @param {string} name
     * @param {PIXI.Point} position
     */
    createLocationButton(phase, id, name, position) {
        const button = new LocationButton(name);
        button.position = position;
        button.signals.click.add(() => {
            P3.audio.playSound("vo_bert_huh_00");
            this.signals.next.dispatch(id);

            console.log(id);

            // tracking
            common.tracking.trackEvent(definitions.TRACKING.EVENT_TYPE.PAGE_VIEW, {
                //pageName: common.tracking.url,
                section: common.tracking.getSection(id),
                pageviewevent: 1,
                progress: common.tracking.progress
            });

            if (common.saveData.activeSlot.unlocks.indexOf(id) < 0 && phase > 1) {
                common.saveData.activeSlot.unlocks.push(id);
                common.saveData.saveGame();
            }
        });
        this._buttonLayer.view.addChild(button);
        return button;
    }

    /**
     * @param {string} name
     */
    createTitleBanner(name) {
        this._titleBanner = new PIXI.Sprite(texture("title_map"));
        this._titleBanner.anchor.set(0.5);
        this._titleBanner.x = common.STAGE_WIDTH/2;
        this._titleBanner.y = 100;
        this.addChild(this._titleBanner);

        if (!iswebfont()) {
            this._titleBanner.label = new PIXI.BitmapText(name, {
                fontName: "Alghera Pro",
                fontSize: 52,
                align: "left",
                tint: 0x0
            });
        } else {
            this._titleBanner.label = new PIXI.Text(name, {
                fontFamily: "FredFredBurger",
                fontSize: 52,
                align: "left",
                fill: 0x0
            });
        }
        this._titleBanner.label.position.set(0, !iswebfont() ? -16 : -22);
        this._titleBanner.label.anchor.set(0.5);
        this._titleBanner.addChild(this._titleBanner.label);

        resizeLabelToFitText(this._titleBanner.label, 280);
    }

    /**
     * @param {PIXI.Point} parallax
     * @returns {P3.CameraLayer}
     * @private
     */
    _addEmptyLayer(parallax) {
        const container = new PIXI.Container();
        this.addChild(container);

        const layer = new P3.CameraLayer(container, parallax);
        this._camera.addLayer(layer);

        return layer;
    }

    _enableZoomButtons() {
        this._zoomInButton.enable = true;
        this._zoomOutButton.enable = true;
    }

    onZoomInButtonClick() {
        if (this._camera.zoom >= 1.4) {
            this._camera.zoom = 1.4;
            return;
        }

        this._defaultTween = gsap.timeline();
        this.addTween(this._defaultTween);

        this._defaultTween.add(gsap.to(this._camera, 0.3, {
            zoom: this._camera.zoom + 0.13,
            ease: Power1.easeInOut,
        }), 0);
    }

    onZoomOutButtonClick() {
        if (this._camera.zoom <= 1.0) {
            this._camera.zoom = 1.0;
            return;
        }

        this._defaultTween = gsap.timeline();
        this.addTween(this._defaultTween);

        this._defaultTween.add(gsap.to(this._camera, 0.3, {
            zoom: this._camera.zoom - 0.13,
            ease: Power1.easeInOut,
        }), 0);
    }
    
    onCloseButtonClick() {
        this.signals.previous.dispatch();
    }
}

/**
 * @static
 * @type {MapScene}
 */
MapScene.active = null;